import React, { useState } from 'react';
import { Calendar, Clock, FileText, CheckCircle, XCircle, AlertCircle, Trash2, Eye } from 'lucide-react';
import AbsenceDetails from './AbsenceDetails';
import { ESTADOS_AUSENCIAS, CATEGORIAS_AUSENCIAS, formatearRangoFechas } from '../../../api/absences';

export default function AbsencesList({
  absences,
  loading,
  error,
  currentUser,
  filters,
  setFilters,
  types,
  onEdit,
  onAprobar,
  onRechazar,
  onCancelar
}) {
  const [selectedAbsence, setSelectedAbsence] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700">Error: {error}</p>
      </div>
    );
  }

  const getEstadoBadge = (estado) => {
    const config = ESTADOS_AUSENCIAS[estado] || ESTADOS_AUSENCIAS.pendiente;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${config.bgColor} ${config.textColor}`}>
        {config.label}
      </span>
    );
  };

  const getCategoriaBadge = (categoria) => {
    const config = CATEGORIAS_AUSENCIAS[categoria] || CATEGORIAS_AUSENCIAS.permiso;
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-xs">
        <span>{config.icon}</span>
        <span>{config.label}</span>
      </span>
    );
  };

  const canUserEdit = (absence) => {
    // Solo puede editar si es el creador y está pendiente
    return absence.created_by === currentUser?.id && absence.estado === 'pendiente';
  };

  const canUserCancel = (absence) => {
    // Puede cancelar si es el creador y está pendiente o aprobada pero no iniciada
    const isOwner = absence.employee_id === currentUser?.employee_id;
    const canCancel = ['pendiente', 'aprobado'].includes(absence.estado);
    const notStarted = new Date(absence.fecha_inicio) > new Date();
    return isOwner && canCancel && notStarted;
  };

  return (
    <div className="space-y-4">
      {/* Filtros */}
      <div className="flex flex-wrap gap-4 mb-6">
        <select
          value={filters.estado}
          onChange={(e) => setFilters({ ...filters, estado: e.target.value })}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
        >
          <option value="">Todos los estados</option>
          <option value="pendiente">Pendiente</option>
          <option value="aprobado">Aprobado</option>
          <option value="rechazado">Rechazado</option>
          <option value="cancelado">Cancelado</option>
          <option value="completado">Completado</option>
        </select>

        <select
          value={filters.tipo}
          onChange={(e) => setFilters({ ...filters, tipo: e.target.value })}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
        >
          <option value="">Todos los tipos</option>
          {types.map((type) => (
            <option key={type.id} value={type.id}>
              {type.nombre}
            </option>
          ))}
        </select>
      </div>

      {/* Lista */}
      {absences.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">No hay ausencias registradas</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {absences.map((absence) => (
            <div
              key={absence.id}
              className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-800">
                      {absence.tipo_nombre}
                    </h3>
                    {getEstadoBadge(absence.estado)}
                    {getCategoriaBadge(absence.tipo_categoria)}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3 text-sm">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Calendar className="w-4 h-4" />
                      <span>{formatearRangoFechas(absence.fecha_inicio, absence.fecha_fin)}</span>
                    </div>

                    <div className="flex items-center gap-2 text-gray-600">
                      <Clock className="w-4 h-4" />
                      <span>{absence.dias_laborables} días laborables</span>
                    </div>

                    <div className="flex items-center gap-2 text-gray-600">
                      <FileText className="w-4 h-4" />
                      <span>{absence.empleado_nombre}</span>
                    </div>

                    {absence.total_documentos > 0 && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <FileText className="w-4 h-4" />
                        <span>{absence.total_documentos} documento(s) adjunto(s)</span>
                      </div>
                    )}
                  </div>

                  {absence.motivo && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-700">
                        <span className="font-semibold">Motivo:</span> {absence.motivo}
                      </p>
                    </div>
                  )}

                  {/* Flujo de aprobación */}
                  {absence.flujo_aprobacion && absence.niveles_aprobacion_total > 0 && (
                    <div className="mt-3 flex items-center gap-2">
                      <span className="text-xs text-gray-500">Aprobación:</span>
                      {absence.flujo_aprobacion.map((nivel, idx) => (
                        <React.Fragment key={idx}>
                          <span
                            className={`text-xs px-2 py-1 rounded ${
                              idx < absence.nivel_aprobacion_actual
                                ? 'bg-green-100 text-green-700'
                                : idx === absence.nivel_aprobacion_actual
                                ? 'bg-orange-100 text-orange-700 font-bold'
                                : 'bg-gray-100 text-gray-500'
                            }`}
                          >
                            {nivel}
                          </span>
                          {idx < absence.flujo_aprobacion.length - 1 && (
                            <span className="text-gray-400">→</span>
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  )}
                </div>

                {/* Acciones */}
                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => {
                      setSelectedAbsence(absence);
                      setShowDetails(true);
                    }}
                    className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Ver detalles"
                  >
                    <Eye className="w-5 h-5" />
                  </button>

                  {onAprobar && absence.estado === 'pendiente' && (
                    <button
                      onClick={() => {
                        const comentarios = prompt('Comentarios (opcional):');
                        if (comentarios !== null) {
                          onAprobar(absence.id, comentarios);
                        }
                      }}
                      className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                      title="Aprobar"
                    >
                      <CheckCircle className="w-5 h-5" />
                    </button>
                  )}

                  {onRechazar && absence.estado === 'pendiente' && (
                    <button
                      onClick={() => {
                        const motivo = prompt('Motivo del rechazo (requerido):');
                        if (motivo && motivo.trim()) {
                          onRechazar(absence.id, motivo);
                        } else if (motivo !== null) {
                          alert('Debe proporcionar un motivo para rechazar');
                        }
                      }}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Rechazar"
                    >
                      <XCircle className="w-5 h-5" />
                    </button>
                  )}

                  {canUserCancel(absence) && (
                    <button
                      onClick={() => {
                        const motivo = prompt('Motivo de la cancelación (opcional):');
                        if (motivo !== null) {
                          onCancelar(absence.id, motivo);
                        }
                      }}
                      className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                      title="Cancelar solicitud"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de detalles */}
      {showDetails && selectedAbsence && (
        <AbsenceDetails
          absence={selectedAbsence}
          onClose={() => {
            setShowDetails(false);
            setSelectedAbsence(null);
          }}
        />
      )}
    </div>
  );
}
